/******************************************** STYLING *********************************************/
const s = require('./autocomplete.scss');

/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import cn from 'classnames';
import AutocompleteIn from 'react-autocomplete';
import {InputChangeEvent} from 'mad-utils/lib/react';

/**************************************** TYPE DEFINITIONS ****************************************/
/**
 * Typings for component exported by react-autocomplete module
 */
interface ReactAutocompleteProps<T> {
    /***** REQUIRED *****/
    /**
     * Used to read the display value from each entry in items
     */
    getItemValue: (item: any) => string;

    /**
     * Items to display in the dropdown menu
     */
    items: T[];

    /**
     * Invoked for each entry in items that also passes shouldItemRender to
     * generate the render tree for each item in the dropdown menu
     *
     * styles is an optional set of styles that can be applied to improve the
     * look/feel of the items in the dropdown menu
     */
    renderItem: (
        item: any,
        isHighlighted: boolean,
        styles?: React.CSSProperties
    ) => React.ReactElement<any>;

    /***** OPTIONAL *****/
    /**
     * Whether to automatically highlight the top match in the dropdown menu
     */
    autoHighlight?: boolean;

    /**
     * Props passed to props.renderInput
     * By default these props will be applied to the <input /> element rendered
     * by Autocomplete, unless you specify a custom val for props.renderInput
     *
     * Any properties supported by HTMLInputElement can be specified, apart
     * from the following, which are set by Autocomplete:
     *     value
     *     autoComplete
     *     role
     *     aria-autocomplete
     *
     * inputProps is commonly used for (but not limited to) setting
     * placeholders, event handlers (e.g. onFocus, onBlur), autoFocus, etc
     */
    inputProps?: React.HTMLAttributes<HTMLInputElement>;

    /**
     * Invoked when attempting to select an item
     * @return {boolean} used to determine whether the item should be selectable
     *                   or not (Default: all items selectable)
     */
    isItemSelectable?: (item: any) => boolean;

    /**
     * Styles that are applied to the dropdown menu in the default renderMenu
     * implementation
     *
     * If you override renderMenu and you want to use menuStyle you must manually
     * apply them (this.props.menuStyle)
     */
    menuStyle?: React.CSSProperties;

    /**
     * Invoked every time the user changes the input's value
     */
    onChange?: (event: InputChangeEvent, value: string) => any;

    /**
     * Invoked every time the dropdown menu's visibility changes (i.e. every
     * time it is displayed/hidden)
     */
    onMenuVisibilityChange?: (isOpen: boolean) => any;

    /**
     * Invoked when the user selects an item from the dropdown menu
     */
    onSelect?: (value: string, item: any) => any;

    /**
     * Used to override the internal logic which displays/hides the dropdown
     * menu, for forcing a certain state based on your UX/business logic
     * Use together with onMenuVisibilityChange for fine-grained control over
     * the dropdown menu dynamics
     */
    open?: boolean;

    /**
     * Invoked to generate the input element
     *
     * The props argument is the result of merging props.inputProps with a
     * selection of props that are required both for functionality and
     * accessibility
     *
     * At minimum you need to apply this props.ref and all props.on<event>
     * event handlers - failing to do this will cause Autocomplete to behave
     * unexpectedly
     */
    renderInput?: (props: Object) => JSX.Element | React.ReactElement<any>;

    /**
     * Invoked to generate the render tree for the dropdown menu
     *
     * Ensure the returned tree includes every entry in items or else the
     * highlight order and keyboard navigation logic will break
     * Styles will contain {top, left, minWidth} which are the coordinates of
     * the top-left corner and the width of the dropdown menu
     */
    renderMenu?: (
        items: T[],
        value: string,
        styles: React.CSSProperties
    ) => JSX.Element | React.ReactElement<any>;

    /**
     * Whether to auto-select the highlighted item when the <input> loses focus
     * Default: false
     */
    selectOnBlur?: boolean;

    /**
     * Invoked for each entry in items and its return value is used to
     * determine whether it should be displayed in the dropdown menu
     * Default: all items always rendered
     */
    shouldItemRender?: (item: T, value: string) => boolean;

    /**
     * Function used to sort items before display
     *
     * Example with a relevancy-based sorting method:
     *     States are sorted based on the location of the match - for example,
     *     a search for "or" returns "Oregon" before "North Carolina" even
     *     though "North Carolina" normally sorts above Oregon
     *     Strings where the match is in the same location (or there is no
     *     match) will be sorted alphabetically - e.g., a search for "or" would
     *     return "North Carolina" above "North Dakota"
     *          export function sortStates(a, b, value) {
     *              const aLower = a.name.toLowerCase();
     *              const bLower = b.name.toLowerCase();
     *              const valueLower = value.toLowerCase();
     *              const queryPosA = aLower.indexOf(valueLower);
     *              const queryPosB = bLower.indexOf(valueLower);
     *              if (queryPosA !== queryPosB) return queryPosA - queryPosB;
     *              return aLower < bLower ? -1 : 1;
     *          }
     */
    sortItems?: (itemA: T, itemB: T, value: string) => -1 | 1;

    /**
     * Value to display in the input field. Default: ``
     */
    value?: T;

    /**
     * Props applied to <div> that wraps <input/> & dropdown menu elements
     * Autocomplete renders
     * DOES NOT add props to <input> itself (See inputProps for that) - it's for
     * the div around it
     * Also doesn't add props to each dropdown item
     */
    wrapperProps?: React.HTMLAttributes<HTMLDivElement>;

    /**
     * Shorthand for wrapperProps={{style: <your styles>}}
     * Note that wrapperStyle is applied before wrapperProps, so the latter
     * will win if it contains a style entry
     */
    wrapperStyle?: React.CSSProperties;
}

/**
 * Accurate props for React Autocomplete class type (from libary component)
 */
declare class IReactAutocomplete extends React.Component<ReactAutocompleteProps<any>> {}

/**
 * CIAutocomplete-specific helper type for omitting given keys from an interface
 */
type CIAutocompOmit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

/**
 * Wrapper props modifies above props to make renderItem optional as well as
 * adding optional type for getting display value from search object
 */
interface CIAutocompleteProps extends CIAutocompOmit<ReactAutocompleteProps<any>, 'renderItem'> {
    getItemDisplayValue?: (item: any) => string;
    keySuffix?: string;
    renderItem?: (
        item: any,
        isHighlighted: boolean,
        styles?: React.CSSProperties
    ) => React.ReactElement<any>;
}

/********************************************* EXPORT *********************************************/
/**
 * Export typed/named React Autocomplete libary component for standalone use
 */
export const ReactAutocomplete = AutocompleteIn as typeof IReactAutocomplete;

ReactAutocomplete.displayName = `ReactAutocomplete`;

/**
 * TODO Refactor CIAutocomplete
 * Wrapper around ReactAutocomplete component to maintain consistent styling
 * Props can be overriden for custom styling/functionality
 *
 * Code generated by ReactAutocomplete:
 *     Component:
 *         <div {...wrapperProps} style={wrapperStyle}>
 *             <input {...inputProps} value={value} /> // < Optionally generated by renderInput
 *         </div>
 *
 *     Menu items (below component):
 *         // \/-- Optionally generated by renderMenu
 *         <div style={menuStyle}>
 *             {renderItem()} // < Optionally sorted by sortItems; on click, runs onSelect
 *         </div>
 *
 *     On clicking item in dropdown, selects it
 */
export const CIAutocomplete = (props: CIAutocompleteProps) => {
    const {inputProps, wrapperProps, getItemDisplayValue, keySuffix, ...passThroughProps} = props;
    const getDisplayValue = getItemDisplayValue || props.getItemValue;

    const buildWrapperKey = item =>
        `ac-item-${props
            .getItemValue(item)
            .replace(/\s+/g, `-`)
            .toLowerCase()}`;

    return (
        <ReactAutocomplete
            renderItem={(item, isHighlighted, _styles) => {
                return (
                    <div
                        key={buildWrapperKey(item)}
                        className={cn(s[`suggestion`], isHighlighted && s[`cursor`])}
                    >
                        {getDisplayValue(item)}
                    </div>
                );
            }}
            renderMenu={(items, _value, _styles) => (
                <div className={cn(s[`dropdown-menu`], props.items.length < 1 && s[`empty`])}>
                    {items}
                </div>
            )}
            wrapperProps={{
                ...wrapperProps,
                className: cn(s[`root`], (wrapperProps && wrapperProps.className) || ``),
            }}
            inputProps={{
                ...inputProps,
                className: cn(s[`input`], (inputProps && inputProps.className) || ``),
            }}
            {...passThroughProps}
        />
    );
};

CIAutocomplete.displayName = `CIAutocomplete`;
